import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const quickAddCompactMediaQuery = window.matchMedia('(max-width: 989px)');
const quickAddHoverPreloadMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const quickAddProgressControllers = new WeakMap();
const quickAddDeferredMediaControllers = new WeakMap();
const quickAddContentCache = new Map();
const quickAddRequestCache = new Map();
let quickAddActiveRequestId = 0;
const quickAddTransparentImage =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

function stageQuickAddMedia(root) {
  const slides = Array.from(root.querySelectorAll('.product-information__media slideshow-slide'));

  slides.forEach((slide, slideIndex) => {
    for (const image of slide.querySelectorAll('img')) {
      if (!(image instanceof HTMLImageElement)) continue;

      if (slideIndex === 0) {
        image.loading = 'eager';
        image.fetchPriority = 'high';
        continue;
      }

      const source = image.getAttribute('src');
      const sourceSet = image.getAttribute('srcset');

      if (source) image.dataset.quickAddSrc = source;
      if (sourceSet) image.dataset.quickAddSrcset = sourceSet;

      image.removeAttribute('srcset');
      image.src = quickAddTransparentImage;
      image.loading = 'lazy';
      image.fetchPriority = 'low';
    }
  });
}

function hydrateQuickAddSlide(slide) {
  if (!(slide instanceof HTMLElement)) return;

  for (const image of slide.querySelectorAll('img[data-quick-add-src], img[data-quick-add-srcset]')) {
    if (!(image instanceof HTMLImageElement)) continue;

    const source = image.dataset.quickAddSrc;
    const sourceSet = image.dataset.quickAddSrcset;

    if (sourceSet) image.setAttribute('srcset', sourceSet);
    if (source) image.src = source;

    delete image.dataset.quickAddSrc;
    delete image.dataset.quickAddSrcset;
  }
}

function setupQuickAddDeferredMedia(root) {
  const productMedia = root.querySelector('.product-information__media');
  if (!(productMedia instanceof HTMLElement)) return;

  quickAddDeferredMediaControllers.get(productMedia)?.abort();
  const controller = new AbortController();
  quickAddDeferredMediaControllers.set(productMedia, controller);

  const slides = Array.from(productMedia.querySelectorAll('slideshow-slide'));
  if (slides.length < 2) return;

  const isCompact = quickAddCompactMediaQuery.matches;
  const scrollSurface = isCompact ? productMedia.querySelector('slideshow-slides') : productMedia;
  if (!(scrollSurface instanceof HTMLElement)) return;

  hydrateQuickAddSlide(slides[0]);

  const getCurrentIndex = () => {
    const itemSize = isCompact ? scrollSurface.clientWidth : slides[0]?.getBoundingClientRect().height;
    const position = isCompact ? Math.abs(scrollSurface.scrollLeft) : scrollSurface.scrollTop;
    if (!itemSize) return 0;
    return Math.min(slides.length - 1, Math.max(0, Math.round(position / itemSize)));
  };

  const hydrateAroundCurrent = () => {
    const currentIndex = getCurrentIndex();
    hydrateQuickAddSlide(slides[currentIndex]);
    hydrateQuickAddSlide(slides[currentIndex - 1]);
    hydrateQuickAddSlide(slides[currentIndex + 1]);
  };

  scrollSurface.addEventListener('pointerdown', hydrateAroundCurrent, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('touchstart', hydrateAroundCurrent, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('wheel', hydrateAroundCurrent, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('scroll', hydrateAroundCurrent, { passive: true, signal: controller.signal });
}

/**
 * Adds a transient position indicator to quick-add media. It follows the
 * native scroll surface, appears only while the customer interacts, and then
 * fades away without changing slideshow behaviour.
 * @param {Element} root
 */
function setupQuickAddMediaProgress(root) {
  const productMedia = root.querySelector('.product-information__media');
  if (!(productMedia instanceof HTMLElement)) return;

  quickAddProgressControllers.get(productMedia)?.abort();
  const controller = new AbortController();
  quickAddProgressControllers.set(productMedia, controller);

  const isCompact = quickAddCompactMediaQuery.matches;
  const scrollSurface = isCompact ? productMedia.querySelector('slideshow-slides') : productMedia;
  if (!(scrollSurface instanceof HTMLElement)) return;

  const progressHost = isCompact ? productMedia : productMedia.closest('.quick-add-modal__content');
  if (!(progressHost instanceof HTMLElement)) return;

  root.querySelector('.quick-add-media-progress')?.remove();

  const progress = document.createElement('div');
  progress.className = 'quick-add-media-progress';
  progress.setAttribute('aria-hidden', 'true');
  progress.innerHTML = '<span class="quick-add-media-progress__value"></span>';
  progressHost.appendChild(progress);

  let hideTimer = 0;

  const update = () => {
    const scrollSize = isCompact ? scrollSurface.scrollWidth : scrollSurface.scrollHeight;
    const clientSize = isCompact ? scrollSurface.clientWidth : scrollSurface.clientHeight;
    const scrollPosition = isCompact ? Math.abs(scrollSurface.scrollLeft) : scrollSurface.scrollTop;
    const hasOverflow = scrollSize > clientSize + 2;

    progress.hidden = !hasOverflow;
    if (!hasOverflow) return;

    const trackSize = isCompact ? progress.clientWidth : progress.clientHeight;
    if (trackSize <= 0) return;

    const visibleRatio = Math.min(1, clientSize / scrollSize);
    const minimumThumbSize = isCompact ? 28 : 22;
    const thumbSize = Math.min(trackSize, Math.max(minimumThumbSize, trackSize * visibleRatio));
    const maximumScroll = Math.max(scrollSize - clientSize, 1);
    const maximumThumbOffset = Math.max(trackSize - thumbSize, 0);
    const positionRatio = Math.min(1, Math.max(0, scrollPosition / maximumScroll));

    progress.style.setProperty('--quick-add-media-thumb-size', `${thumbSize}px`);
    progress.style.setProperty('--quick-add-media-thumb-offset', `${maximumThumbOffset * positionRatio}px`);
  };

  const showTemporarily = () => {
    update();
    if (progress.hidden) return;

    progress.classList.add('is-active');
    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(() => progress.classList.remove('is-active'), 720);
  };

  scrollSurface.addEventListener('scroll', showTemporarily, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('pointerdown', showTemporarily, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('touchstart', showTemporarily, { passive: true, signal: controller.signal });
  scrollSurface.addEventListener('wheel', showTemporarily, { passive: true, signal: controller.signal });

  const resizeObserver = new ResizeObserver(update);
  resizeObserver.observe(scrollSurface);
  controller.signal.addEventListener('abort', () => {
    window.clearTimeout(hideTimer);
    resizeObserver.disconnect();
  });

  requestAnimationFrame(update);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #cartUpdateAbortController = null;
  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    quickAddCompactMediaQuery.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerenter', this.prefetchProductContent, {
      passive: true,
      signal: this.#cartUpdateAbortController.signal,
    });
    this.addEventListener('focusin', this.prefetchProductContent, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    quickAddCompactMediaQuery.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController?.abort();
    this.#cartUpdateAbortController = null;
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  prefetchProductContent = () => {
    if (this.dataset.quickAddButton !== 'choose') return;
    if (!quickAddHoverPreloadMediaQuery.matches) return;

    const currentUrl = this.productPageUrl;
    if (!currentUrl || quickAddContentCache.has(currentUrl) || quickAddRequestCache.has(currentUrl)) return;

    void this.#getProductGrid(currentUrl).catch(() => undefined);
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    quickAddContentCache.clear();
  };

  async #getProductGrid(currentUrl) {
    const cachedContent = quickAddContentCache.get(currentUrl);
    if (cachedContent) return cachedContent;

    let pendingRequest = quickAddRequestCache.get(currentUrl);
    if (!pendingRequest) {
      pendingRequest = this.fetchProductPage(currentUrl)
        .then((html) => {
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) return null;

          const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          quickAddContentCache.set(currentUrl, productGrid);
          return productGrid;
        })
        .finally(() => quickAddRequestCache.delete(currentUrl));
      quickAddRequestCache.set(currentUrl, pendingRequest);
    }

    return pendingRequest;
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const trigger = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null;
    const requestId = ++quickAddActiveRequestId;
    const cachedProductGrid = quickAddContentCache.get(currentUrl);

    trigger?.setAttribute('aria-busy', 'true');

    try {
      if (!cachedProductGrid) {
        this.#renderLoadingShell();
        this.#openQuickAddModal();
      }

      const productGrid = await this.#getProductGrid(currentUrl);
      if (!productGrid || requestId !== quickAddActiveRequestId) return;

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      if (cachedProductGrid) {
        this.#openQuickAddModal();
      } else {
        requestAnimationFrame(() => this.#resetScroll());
      }
    } catch (error) {
      if (requestId === quickAddActiveRequestId) {
        this.#closeQuickAddModal();
        window.location.assign(currentUrl);
      }
      if (error?.name !== 'AbortError') console.warn('[quick-add] Unable to load product options:', error);
    } finally {
      trigger?.removeAttribute('aria-busy');
    }
  };

  #renderLoadingShell() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    const productCard = this.closest('product-card');
    const sourceImage = productCard?.querySelector(
      '.jesoli-product-card__media-layer--primary img, [ref="cardGallery"] img, .card-gallery img'
    );
    const sourcePrice = productCard?.querySelector('.jesoli-product-card__price, product-price');

    const shell = document.createElement('div');
    shell.className = 'quick-add-loading';
    shell.setAttribute('role', 'status');
    shell.setAttribute('aria-live', 'polite');

    const media = document.createElement('div');
    media.className = 'quick-add-loading__media';
    if (sourceImage instanceof HTMLImageElement) {
      const image = document.createElement('img');
      image.className = 'quick-add-loading__image';
      image.src = sourceImage.currentSrc || sourceImage.src;
      image.alt = sourceImage.alt;
      image.loading = 'eager';
      media.appendChild(image);
    } else {
      media.classList.add('quick-add-loading__placeholder');
    }

    const details = document.createElement('div');
    details.className = 'quick-add-loading__details';

    const heading = document.createElement('p');
    heading.className = 'quick-add-loading__title';
    heading.textContent = this.dataset.productTitle || '';

    const price = document.createElement('p');
    price.className = 'quick-add-loading__price';
    price.textContent = sourcePrice?.textContent?.trim() || '';

    const skeleton = document.createElement('div');
    skeleton.className = 'quick-add-loading__skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    for (const size of ['short', 'full', 'full', 'action']) {
      const line = document.createElement('span');
      line.className = `quick-add-loading__line quick-add-loading__line--${size}`;
      skeleton.appendChild(line);
    }

    details.append(heading, price, skeleton);
    shell.append(media, details);
    modalContent.replaceChildren(shell);
    modalContent.classList.add('quick-add-modal__content--loading');
    modalContent.setAttribute('aria-busy', 'true');
    modalContent.setAttribute('aria-label', this.dataset.loadingLabel || '');
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const mediaSlides = productMedia?.querySelector('slideshow-slides');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    mediaSlides?.scrollTo({ left: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
    requestAnimationFrame(() => requestAnimationFrame(() => this.#resetScroll()));
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    stageQuickAddMedia(productGrid);

    const productDetails = productGrid.querySelector('.product-details');
    const inlineProductDetails = productGrid.querySelector('.product-information__inline-details');
    // Treat narrow tablets and unfolded foldables as a compact purchase surface.
    // The theme's default 750px breakpoint left these devices in the desktop
    // two-column modal even when there was not enough usable width.
    const isCompact = quickAddCompactMediaQuery.matches;

    if (!isCompact && productDetails && inlineProductDetails) {
      productDetails.appendChild(inlineProductDetails);
    }

    if (isCompact) {
      const productMedia = productGrid.querySelector('.product-information__media');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      // Keep the mobile reading and focus order aligned with the visual purchase flow:
      // media, title/price, variants, purchase actions, then product details.
      if (productMedia) {
        // Quick add uses the existing product slideshow for native touch
        // scrolling. Remove zoom actions so a swipe/tap inside the sheet does
        // not unexpectedly open the product-page lightbox.
        for (const slide of productMedia.querySelectorAll('slideshow-slide')) {
          slide.removeAttribute('on:click');
        }
        productMedia.querySelector('zoom-dialog')?.remove();
        productGrid.appendChild(productMedia);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (inlineProductDetails) {
        productGrid.appendChild(inlineProductDetails);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    modalContent.classList.remove('quick-add-modal__content--loading');
    modalContent.removeAttribute('aria-busy');
    modalContent.removeAttribute('aria-label');

    this.#syncVariantSelection(modalContent);
    setupQuickAddDeferredMedia(modalContent);
    setupQuickAddMediaProgress(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  #viewportAnimationFrame;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#stopViewportSync();
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

        requestAnimationFrame(() => setupQuickAddMediaProgress(this));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    quickAddActiveRequestId += 1;
    this.#stopViewportSync();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  #handleDialogOpen = () => {
    this.#startViewportSync();
  };

  #startViewportSync() {
    if (CSS.supports('height', '100dvh')) return;

    this.#updateViewportHeight();
    window.addEventListener('resize', this.#scheduleViewportUpdate, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#scheduleViewportUpdate, { passive: true });
    window.visualViewport?.addEventListener('scroll', this.#scheduleViewportUpdate, { passive: true });
  }

  #stopViewportSync() {
    window.removeEventListener('resize', this.#scheduleViewportUpdate);
    window.visualViewport?.removeEventListener('resize', this.#scheduleViewportUpdate);
    window.visualViewport?.removeEventListener('scroll', this.#scheduleViewportUpdate);

    if (this.#viewportAnimationFrame !== undefined) {
      cancelAnimationFrame(this.#viewportAnimationFrame);
      this.#viewportAnimationFrame = undefined;
    }
  }

  #scheduleViewportUpdate = () => {
    if (this.#viewportAnimationFrame !== undefined) return;

    this.#viewportAnimationFrame = requestAnimationFrame(() => {
      this.#viewportAnimationFrame = undefined;
      this.#updateViewportHeight();
    });
  };

  #updateViewportHeight() {
    const { dialog } = this.refs;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

    dialog.style.setProperty('--quick-add-viewport-height', `${Math.round(viewportHeight)}px`);
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
